import ChangeStatusModal from '#pages/order/ChangeStatus/ChangeStatusModal';
import OrderNoteModal from '#pages/order/OrderNote';
import { useIsMutating } from '@tanstack/react-query';
import {
  useDeleteOrder,
  useGetDeviceStatisticsInterval,
  useGetOrderNotes,
  useGetOrdersInterval,
  useUpdateOrderNote,
  useUpdateOrderPriority,
} from '@vindr/services/actions/order.action';
import { ParamsGetOrders, ResponseGetOrders } from '@vindr/services/apis/order.api';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import {
  ORDER_COLUMNS_ORIGINAL,
  ORDER_COLUMNS_SETTING,
  ORDER_PRIORITY,
} from '@vindr/services/constants/order.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { queryKey } from '@vindr/services/constants/queryClientKey';
import { PaginationProps } from '@vindr/services/interfaces';
import { SidePanel, SiderLayout, Table } from '@vindr/ui';
import useDragResizeColumn from '@vindr/ui/src/hooks/useDragResizeColumn';
import { addToArray, filterValidValue } from '@vindr/utils/helpers/common.helper';
import useToggle from '@vindr/utils/hooks/useToggle';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import { useGlobalState } from '@vindr/workspace-core/contexts/GlobalContext';
import Assign from '@vindr/workspace-core/modules/Assign';
import CreateOrder from '@vindr/workspace-core/modules/CreateOrder';
import ReceiveQMS from '@vindr/workspace-core/modules/ReceiveQMS/ReceiveQMS';
import EditOrder from '@vindr/workspace-core/modules/Order/EditOrder';
import dayjs from 'dayjs';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ReactDragListView from 'react-drag-listview';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import DeliverModal from './Deliver';
import Filter from './Filter';
import OrderListHeader from './OrderListHeader';
import ReceptionWidget from './ReceptionWidget';
import useContextMenu, { MENU_TYPE } from './useContextMenu';
import useDefinedColumns from './useDefinedColumns';

export const INITIAL_SORTER_ORDER: {
  sort_field: Parameters<typeof useGetOrdersInterval>[0]['sort_field'];
  sort_order: Parameters<typeof useGetOrdersInterval>[0]['sort_order'];
} = {
  sort_field: 'scheduled_date',
  sort_order: SORT_ORDER.DESC,
};

export const INITIAL_FILTER_ORDER: ParamsGetOrders = {
  scheduled_date_from: dayjs().startOf('days').toISOString(),
  scheduled_date_to: dayjs().endOf('days').toISOString(),
  // ...(keycloak.userInfo?.default_institution_id && {
  //   institution_ids: [keycloak.userInfo?.default_institution_id],
  // }),
};

const Order = () => {
  const { t } = useTranslation('Vindoc');
  const { width } = useWindowSize();
  const { message, modal, keycloak } = useGlobalState();
  const [editingOrder, setEditingOrder] = useState<ResponseGetOrders['data'][number]>(null);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [popupAddReception, setPopupAddReception] = useState<{
    open: boolean;
    action: string | null;
    record?: any;
  }>({
    open: false,
    action: null,
  });
  const [popupAddReceptionQms, setPopupAddReceptionQms] = useState<{
    open: boolean;
    record?: any;
  }>({
    open: false,
  });
  const { mutate: deleteOrder } = useDeleteOrder();
  const { mutate: updateOrderPriority, isLoading: isUpdateOrderPriority } =
    useUpdateOrderPriority();
  const { mutate: updateOrderNote, isLoading: isUpdateOrderNote } = useUpdateOrderNote();
  const [selectedRowKeys, setSelectedRowKeys] = useImmer<ResponseGetOrders['data']>([]);
  const [collapsedRight, setCollapsedRight] = useState(window.innerWidth < 640);
  const [collapsed, setCollapsed] = useState(false);
  const {
    value: isOpenOrderNoteModal,
    setTrue: openOrderNoteModal,
    setFalse: closeOrderNoteModal,
  } = useToggle(false);

  const [statusModalState, setStatusModalState] = useState({
    isOpenFromContextMenu: false,
    initStatus: null,
    requestStatus: null,
    open: false,
  });
  const {
    value: isOpenDeliverModal,
    setTrue: openDeliverModal,
    setFalse: closeDeliverModal,
  } = useToggle(false);
  const defaultInstitutionId = keycloak.userInfo?.default_institution_id;

  const isUpdatingProfile = useIsMutating({ mutationKey: [queryKey.update_profile] });
  INITIAL_FILTER_ORDER.institution_ids = addToArray(defaultInstitutionId, undefined);

  useLayoutEffect(() => {
    if (isUpdatingProfile === 0) {
      INITIAL_FILTER_ORDER.institution_ids = addToArray(defaultInstitutionId, undefined);
    }
  }, [defaultInstitutionId, isUpdatingProfile]);

  const [params, setParams] = useState<ParamsGetOrders>(INITIAL_FILTER_ORDER);
  const [sorter, setSorter] = useState(INITIAL_SORTER_ORDER);
  const [assignState, setAssignState] = useState({ visible: false, order: null });
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    size: 25,
  });

  const handleRemove = (record) => {
    modal.confirm({
      className: 'delete-modal',
      title: t(MESSAGE.DIALOG.CFD_8000),
      okText: t('Delete'),
      onOk: () => {
        deleteOrder(
          { id: record?.id },
          {
            onSuccess: () => {
              message.success(t(MESSAGE.SUCCESS.SCD_8002));
            },
            onError: (error: any) => {
              const error_message =
                (error?.response?.data?.error_code &&
                  MESSAGE.ERROR[error?.response?.data?.error_code]) ||
                'Delete error';

              message.error(t(error_message));
            },
          }
        );
      },
    });
  };

  const handleEdit = (record: typeof orders[number]) => {
    setEditingOrder(record);
    setIsEditingOrder(true);
  };

  const handleOpenAssign = (record) => {
    setAssignState({ visible: true, order: record });
  };

  const handleUpdateOrderPriority = (record, selectedPriority) => {
    updateOrderPriority(
      {
        order_id: record.id,
        priority: selectedPriority,
      },
      {
        onSuccess: () => {
          message.success(t(MESSAGE.SUCCESS.SCD_8001));
        },
        onError: (error: any) => {
          const error_message =
            (error?.response?.data?.error_code &&
              MESSAGE.ERROR[error?.response?.data?.error_code]) ||
            'Update error';
          message.error(t(error_message));
        },
      }
    );
  };

  const handleSaveNewNote = (note) => {
    updateOrderNote(
      {
        id: editingOrder?.id,
        notes: [note],
      },
      {
        onSuccess: () => {
          message.success(t(MESSAGE.SUCCESS.SCD_8001));
        },
        onError: (error: any) => {
          const error_message =
            (error?.response?.data?.error_code &&
              MESSAGE.ERROR[error?.response?.data?.error_code]) ||
            'Update error';

          message.error(t(error_message));
        },
      }
    );
  };

  const handleOpenModalOrderNote = (record) => {
    setEditingOrder(record);
    openOrderNoteModal();
  };

  const handleOpenChangeStatusModal = (record, selectedStatus) => {
    setEditingOrder({
      ...record,
      status: selectedStatus,
    });
    setStatusModalState({
      isOpenFromContextMenu: true,
      requestStatus: selectedStatus,
      initStatus: null,
      open: true,
    });
  };

  const handleOpenModalDeliver = (record) => {
    setEditingOrder(record);
    openDeliverModal();
  };

  const performedColumns = useDefinedColumns({ pagination });

  const {
    components,
    dragProps,
    getDefaultCol,
    resetColumns,
    resizableColumns,
    setVisibleColumns,
    tableWidth,
    visibleColumns,
  } = useDragResizeColumn(performedColumns, ORDER_COLUMNS_SETTING, ORDER_COLUMNS_ORIGINAL);

  const data = useGetOrdersInterval({
    offset: (pagination.page - 1) * pagination.size,
    limit: pagination.size,
    ...filterValidValue({ ...sorter, ...params }, { invalidList: ['', undefined, null] }),
  });

  const count = useMemo(() => data?.data?.count || 0, [data]);

  const orders = useMemo(() => data?.data?.data || [], [data]);

  const isLoading = data?.isLoading;

  const isFetching = data?.isFetching;

  const refetch = data?.refetch;

  const [isRefetch, setIsRefetch] = useState(false);

  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = (params: ParamsGetOrders) => {
    handleFilter(params);
    // Set refetch to true only when force search
    setIsRefetch(true);
    refetch?.();
  };

  const handleFilter = (p: ParamsGetOrders, type?: 'status' | 'modalities') => {
    setPagination({ ...pagination, page: 1 });
    setParams(p);
    setSelectedRowKeys(() => []);

    //? on mobile, not close when choose select multiple
    if (['status', 'modalities'].includes(type)) return;

    setCollapsedRight(true);
  };

  const orderNotes = useGetOrderNotes(editingOrder?.id, {
    enabled:
      isOpenOrderNoteModal &&
      editingOrder?.id &&
      keycloak.hasOneOfPerm([PERMISSIONS.order.view, PERMISSIONS.order.note]),
  });

  const orderNotesMemo = useMemo(() => orderNotes?.data?.data || [], [orderNotes]);

  const deviceStatistics =
    useGetDeviceStatisticsInterval({
      offset: (pagination.page - 1) * pagination.size,
      limit: pagination.size,
      ...filterValidValue(
        {
          ...sorter,
          scheduled_date_from: params.scheduled_date_from,
          scheduled_date_to: params.scheduled_date_to,
          modalities: params?.modalities,
          institution_ids: params?.institution_ids,
        },
        { invalidList: ['', undefined, null] }
      ),
    })?.data?.data || [];

  const handleContextMenuSelect = (
    type: keyof typeof MENU_TYPE,
    record: typeof orders[number],
    selectedPriority?: number,
    selectedStatus?: number
  ) => {
    switch (type) {
      case MENU_TYPE.ASSIGN:
        handleOpenAssign(record);
        break;
      case MENU_TYPE.EDIT:
        handleEdit(record);
        break;
      case MENU_TYPE.DELETE:
        handleRemove(record);
        break;
      case MENU_TYPE.PRIORITY:
        handleUpdateOrderPriority(record, selectedPriority);
        break;
      case MENU_TYPE.NOTE:
        handleOpenModalOrderNote(record);
        break;
      case MENU_TYPE.CHANGE_STATUS:
        handleOpenChangeStatusModal(record, selectedStatus);
        break;
      case MENU_TYPE.CHANGE_DEVICE:
        setPopupAddReception({ open: true, action: 'changeDevice', record });
        break;
      case MENU_TYPE.RECEIVE:
        setPopupAddReception({ open: true, action: 'receive', record });
        break;
      case MENU_TYPE.RECEIVE_QMS:
        setPopupAddReceptionQms({ open: true, record });
        break;
      case MENU_TYPE.DELIVER:
        handleOpenModalDeliver(record);
        break;
      default:
        break;
    }
  };

  const contextMenuItems = useContextMenu({
    handleContextMenuSelect,
    selectedRowKeys,
  });

  const filter = (
    <Filter
      className="min-w-[630px] flex-1"
      forceSearch={forceSearch}
      initFilters={INITIAL_FILTER_ORDER}
      onChange={handleFilter}
    />
  );

  const onRow = (record) => {
    return {
      onClick: (event) => {
        event.preventDefault();
        event.stopPropagation();

        setSelectedRowKeys((draft) => {
          const recordIndex = draft.findIndex((item) => item.id === record.id);

          return recordIndex === -1 ? [record] : [];
        });
      },
    };
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys.map((item) => item.id),
    onChange(selectedRowKeys, selectedRows: typeof orders, _) {
      const isSameInstitution = selectedRows.every(
        (item) => item.institution_id === selectedRows[0].institution_id
      );

      if (!isSameInstitution) {
        message.error(t(MESSAGE.ERROR.EMSG_8015));
        return;
      }

      const isSameStatus = selectedRows.every((item) => item.status === selectedRows[0].status);

      if (isSameInstitution && !isSameStatus) {
        message.error(t('Status mismatch, please choose the same status.'));
        return;
      }

      const isSameModality = selectedRows.every((item) => item.procedure.modality === selectedRows[0].procedure.modality);

      if (!isSameModality) {
        message.error(t('Modality mismatch, please choose the same modality.'));
        return;
      }

      setSelectedRowKeys(() => selectedRows);
    },
  };

  const defaultValuesChangeStatus = useMemo(
    () => ({
      order_id: selectedRowKeys?.map((item) => item.id),
      requestStatus: statusModalState.requestStatus,
      initStatus: statusModalState.initStatus,
      priority:
        ORDER_PRIORITY[editingOrder?.priority] ||
        selectedRowKeys.every((item) => item.priority === selectedRowKeys[0]?.priority)
          ? ORDER_PRIORITY[selectedRowKeys[0]?.priority]
          : null,
      notes: editingOrder?.tech_notes,
      isOpenFromContextMenu: statusModalState.isOpenFromContextMenu,
      study_instance_uids: selectedRowKeys
        ?.map((item) => item?.study?.study_instance_uid)
        ?.filter(Boolean),
    }),
    [selectedRowKeys, editingOrder, statusModalState]
  );

  return (
    <SiderLayout
      rightSidebar={
        width < 640 ? (
          <SidePanel
            collapsed={collapsedRight}
            defaultCollapsed={true}
            side="right"
            width={215}
            onChange={(val) => setCollapsedRight(val)}
          >
            <div className="m-2">{filter}</div>
          </SidePanel>
        ) : null
      }
    >
      <div className="flex h-full flex-col overflow-auto rounded-sm bg-background-light-3 p-4 dark:bg-background-dark-3">
        <OrderListHeader
          totalItem={count}
          {...{ selectedRowKeys, setSelectedRowKeys }}
          collapsed={collapsed}
          onCollapse={() => setCollapsed((prev) => !prev)}
        />
        <ReceptionWidget collapsed={collapsed} devices={deviceStatistics} />
        <div className="flex flex-wrap justify-between">
          {width >= 640 && filter}
          {keycloak.hasOneOfPerm([PERMISSIONS.studylist.upload]) && (
            <CreateOrder
              {...{
                selectedRowKeys,
                setSelectedRowKeys,
                params,
                popupAddReception,
                setPopupAddReception,
                setPopupAddReceptionQms,
              }}
              openChangeStatusModal={() =>
                setStatusModalState({
                  initStatus: selectedRowKeys?.[0]?.status,
                  requestStatus: null,
                  open: true,
                  isOpenFromContextMenu: false,
                })
              }
            />
          )}
        </div>
        <div className="flex h-0 flex-1 flex-col">
          <ReactDragListView.DragColumn {...dragProps}>
            <Table
              columns={resizableColumns}
              components={components}
              count={count}
              dataSource={orders as any}
              loading={isLoading || isRefetch || isUpdateOrderNote || isUpdateOrderPriority}
              pagination={pagination}
              rowKey="id"
              rowSelection={rowSelection as any}
              scroll={{ x: tableWidth }}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
              wrapperClassName="min-h-[210px]"
              contextMenu={{
                items: contextMenuItems,
                onClick: (menuItem, record: typeof orders[number]) => {
                  if (menuItem.onClick) {
                    menuItem.onClick(record);
                  }
                },
              }}
              rowCustom={{
                getDefaultCol,
                performedColumns,
                resetColumns,
                setVisibleColumns,
                settingKey: ORDER_COLUMNS_SETTING,
                visibleColumns,
              }}
              onPaginationChange={(p) => p && setPagination(p)}
              onRow={onRow as any}
              onTableChange={(_, __, sorter) => {
                setSorter({
                  sort_field: sorter.field,
                  sort_order: sorter.order === 'ascend' ? SORT_ORDER.ASC : SORT_ORDER.DESC,
                });
              }}
            />
          </ReactDragListView.DragColumn>
        </div>
      </div>
      {assignState.order ? (
        <Assign
          message={message}
          modal={modal}
          order={assignState.order}
          visible={assignState.visible}
          onCancel={() => setAssignState({ visible: false, order: null })}
        />
      ) : null}
      <EditOrder
        isEditing={isEditingOrder}
        message={message}
        orderData={editingOrder}
        onFinish={() => {
          setEditingOrder(null);
          setIsEditingOrder(false);
        }}
      />
      {isOpenOrderNoteModal && (
        <OrderNoteModal
          open={isOpenOrderNoteModal}
          orderNotes={orderNotesMemo}
          onClose={closeOrderNoteModal}
          onSaveNewNote={(note) => handleSaveNewNote(note)}
        />
      )}
      <ChangeStatusModal
        defaultValues={defaultValuesChangeStatus}
        visible={statusModalState.open}
        onCancel={() => {
          setStatusModalState({
            initStatus: null,
            requestStatus: null,
            open: false,
            isOpenFromContextMenu: false,
          });
        }}
        onFinish={() => {
          setStatusModalState({
            initStatus: null,
            requestStatus: null,
            open: false,
            isOpenFromContextMenu: false,
          });
        }}
      />
      {isOpenDeliverModal && (
        <DeliverModal
          open={isOpenDeliverModal}
          order={editingOrder}
          onClose={closeDeliverModal}
          onFinish={closeDeliverModal}
        />
      )}
      {popupAddReceptionQms?.open ? (
        <ReceiveQMS
          open={popupAddReceptionQms.open}
          onCancel={() => setPopupAddReceptionQms({ open: false })}
          {...{ selectedRowKeys, setSelectedRowKeys, params }}
        />
      ) : null}
    </SiderLayout>
  );
};

export default Order;
